import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Scale, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PolicyRuleCard } from "@/components/PolicyRuleCard";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { OutcomeCard } from "@/components/OutcomeCard";
import { StageNotReached } from "@/components/StageNotReached";
import { Card, Spinner } from "@/components/ui/primitives";
import { markStageForPlayback } from "@/lib/playback";
import { usePlayback } from "@/hooks/usePlayback";
import { useAutoAdvance } from "@/hooks/useAutoAdvance";

export const Route = createFileRoute("/apps/$id/coordination")({ component: CoordinationPage });

function CoordinationPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["coordination", id], queryFn: () => api.getCoordination(id) });
  const { data: app } = useQuery({ queryKey: ["application", id], queryFn: () => api.getApplication(id) });

  const advance = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "coordination");
      await qc.invalidateQueries();
    },
  });
  const advanceNext = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "decision");
      await qc.invalidateQueries();
      navigate({ to: "/apps/$id/decision", params: { id } });
    },
  });
  const rerun = useMutation({ mutationFn: () => api.reRunStage(id, "coordination"), onSuccess: () => qc.invalidateQueries() });

  const playback = usePlayback({
    stage: "coordination",
    appId: id,
    events: data?.journey_events ?? [],
    enabled: Boolean(data?.reached),
    cardCount: data?.evaluations.length ?? 0,
  });

  useAutoAdvance({
    appId: id,
    stage: "coordination",
    isCurrent: app?.stage === "coordination",
    playbackStatus: playback.status,
    advanceMutation: advanceNext,
    nextStage: "decision",
  });

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Spinner /> Loading coordination…</div>;
  if (!data) return null;
  if (!data.reached) {
    return (
      <StageNotReached
        title="Coordination hasn't run yet"
        description="The Compliance Agent evaluates each lending-policy rule against the risk-assessed application. Advance the pipeline to produce the policy evaluations."
        primaryLabel="Run policy coordination"
        onPrimary={() => advance.mutate()}
        primaryDisabled={advance.isPending}
      />
    );
  }
  const isCurrent = app?.stage === "coordination";

  const counts = data.evaluations.reduce(
    (acc, e) => ({ ...acc, [e.result]: (acc[e.result] ?? 0) + 1 }),
    { pass: 0, warn: 0, fail: 0 } as Record<string, number>,
  );
  const tone: "success" | "warn" | "destructive" = counts.fail > 0 ? "destructive" : counts.warn > 0 ? "warn" : "success";

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <Scale className="h-5 w-5 text-accent" strokeWidth={1.9} /> Policy Coordination
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The Compliance Agent evaluates every lending-policy rule against the risk-assessed application.
          </p>
        </header>

        <section className="space-y-3">
          {!playback.revealFindings ? (
            <Card className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-accent" strokeWidth={2.2} />
              Compliance Agent is evaluating policy rules…
            </Card>
          ) : (
            data.evaluations.map((e, i) => (
              <PolicyRuleCard
                key={e.id}
                ev={e}
                animateIn={playback.isPlaying}
                delayMs={playback.cardDelayMs(i)}
              />
            ))
          )}
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold">Agent activity</h3>
          <JourneyTimeline events={data.journey_events} playback={playback} />
        </section>
      </div>

      {playback.revealOutcome ? (
        <OutcomeCard
          tone={tone}
          title="Compliance Review Complete"
          primaryLabel={isCurrent ? "Continue to Decision →" : "Go to Decision →"}
          onPrimary={() => (isCurrent ? advanceNext.mutate() : navigate({ to: "/apps/$id/decision", params: { id } }))}
          primaryDisabled={advanceNext.isPending}
          secondaryLabel={isCurrent ? "Re-run evaluation" : undefined}
          onSecondary={isCurrent ? () => rerun.mutate() : undefined}
        >
          <div className="flex items-center justify-between">
            <span>Passes</span>
            <span className="font-semibold text-success">{counts.pass ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Warnings</span>
            <span className="font-semibold text-warn">{counts.warn ?? 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Fails</span>
            <span className="font-semibold text-destructive">{counts.fail ?? 0}</span>
          </div>
        </OutcomeCard>
      ) : (
        <aside className="sticky top-8 rounded-xl border border-accent/30 bg-accent/5 p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-accent">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
            Running policy coordination…
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            The Compliance Agent is walking the policy rule engine against the
            risk-assessed application. Results will appear once every rule has been
            evaluated.
          </p>
        </aside>
      )}
    </div>
  );
}
